import { ClinicSettings } from "@/Common/Api/MedfarDictionaries/Dto/Responses/ClinicSettings";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { StatusComponent } from "@/Common/Components/Patient/StatusComponent";
import { ObjectHelper } from "@/Common/Helpers/ObjectHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BritishColumbiaPatientBannerMspCoverage } from "./HealthCoverageStatus/BritishColumbiaPatientBannerMspCoverage";
import { OntarioPatientBannerEnrollment } from "./HealthCoverageStatus/OntarioPatientBannerEnrollment";

import img_icon_bluestart from "@/Common/_images/Icons/Ramq/BlueStart.png";
import InLongTermCareFacilityIcon from "@/Common/_images/Icons/Ramq/InLongTermHealthCareFacility.svg";

interface IPatientBannerHealthCoverageProps {
    patient: Patient;
    site: SitesModel;
    clinicSettings: ClinicSettings;
    ontarioPatientEnrollmentModel: OntarioPatientEnrollmentModel;
    onEnrollmentStatusClick?: () => void;
}

class PatientBannerHealthCoverage extends BaseReactComponent<IPatientBannerHealthCoverageProps, any> {
    public shouldComponentUpdate(nextProps: IPatientBannerHealthCoverageProps): boolean {
        return this.props.site?.getId() !== nextProps.site?.getId()
            || this.props.onEnrollmentStatusClick !== nextProps.onEnrollmentStatusClick
            || !ObjectHelper.equals(this.props.patient, nextProps.patient)
            || !ObjectHelper.equals(this.props.clinicSettings, nextProps.clinicSettings)
            || !ObjectHelper.equals(this.props.ontarioPatientEnrollmentModel, nextProps.ontarioPatientEnrollmentModel);
    }

    public render(): JSX.Element {
        const checkHealthCard = (province: CanadaState): boolean =>
            !this.props.patient.HealthCards?.any() || this.props.patient.hasHealthCardInProvince(province);

        const renderingWithPreferredSiteInProvince = this.renderWithProvinceCheck(
            p => this.props.site?.isPreferredSiteAddressInProvince(p) && checkHealthCard(p)
        );

        if (renderingWithPreferredSiteInProvince)
            return renderingWithPreferredSiteInProvince;

        const renderingWithSiteInProvince = this.renderWithProvinceCheck(
            p => this.props.site?.isSiteInProvince(p) && checkHealthCard(p)
        );

        if (renderingWithSiteInProvince)
            return renderingWithSiteInProvince;

        const renderingWithPreferredSiteInProvinceNoCardCheck = this.renderWithProvinceCheck(
            p => this.props.site?.isPreferredSiteAddressInProvince(p)
        );

        if (renderingWithPreferredSiteInProvinceNoCardCheck)
            return renderingWithPreferredSiteInProvinceNoCardCheck;

        return this.renderDefault();
    }

    private renderWithProvinceCheck(checkForProvince: (province: CanadaState) => boolean): JSX.Element {
        if (this.props.clinicSettings.EnableOntarioPatientEnrollment && checkForProvince(CanadaState.Ontario))
            return this.renderOntarioEnrollment();

        if (this.props.clinicSettings.EnableMedicalServicesPlanEligibility && checkForProvince(CanadaState.BritishColumbia))
            return this.renderMedicalServicesPlanEligibility();

        if (checkForProvince(CanadaState.Quebec))
            return this.renderDefault();

        return null;
    }

    private renderDefault(): JSX.Element {
        const cvSplitter = IsNullOrWhiteSpace(this.props.patient.GetFormattedRamqCV())
            ? ""
            : " | ";

        return (
            <div>
                {this.props.patient.HasIntervenorOnFile() && (
                    <div
                        className="pointer flex-container flex-start align-bottom"
                        onClick={() => sessionStore.dispatch(LocalPatientOperations.Profile.PreferredIntervenorOnFileClick)}
                    >
                        <img className="margin-xs-left" style={{ width: "12px" }} src={img_icon_bluestart} />
                        <span className="margin-xs-left">
                            {this.props.patient.GetFormattedIntervenorOnFile()}
                        </span>
                    </div>
                )}
                {cacheStore.SpecificClientSettings.HasMedfarRAMQ && !this.props.clinicSettings.EnablePatientSubscription && (
                    <>
                        <StatusComponent
                            status={this.props.patient.GetAssignedDoctorStatus()}
                            onClickStatus={() => this.props.onEnrollmentStatusClick?.()}
                            statusInfo={`${this.props.patient.GetFormattedRamqDoctorOrStatus()}${cvSplitter}${this.props.patient.GetFormattedRamqCV()}`}
                        />
                        {this.props.patient.RAMQInfo?.RamqGl6Info?.isInCHSLD && (
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <InLongTermCareFacilityIcon className="margin-xs-left" style={{ width: "12px" }} />
                                <span>{cacheStore.GetString("RamqInsuredWithLongTermCareFacilityLabel")}</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }

    private renderMedicalServicesPlanEligibility(): JSX.Element {
        return (
            <BritishColumbiaPatientBannerMspCoverage
                patient={this.props.patient}
                dateOfBirth={this.props.patient.IndividualResponse?.DateOfBirth} // needed to update the component after editing the patient's date of birth
            />
        );
    }

    private renderOntarioEnrollment(): JSX.Element {
        return (
            <OntarioPatientBannerEnrollment
                patient={this.props.patient}
                ontarioPatientEnrollmentModel={this.props.ontarioPatientEnrollmentModel}
                onOntarioEnrollmentClick={this.props.onEnrollmentStatusClick}
            />
        );
    }
}

export { PatientBannerHealthCoverage };
